/**
 * PostHog analytics — privacy-first product analytics.
 *
 * Privacy guarantees:
 *   - User IDs are SHA-256 hashed before being sent — PostHog never sees a
 *     raw Supabase UUID or email address.
 *   - Autocapture is disabled (no accidental form-field capture).
 *   - Session recording is disabled.
 *   - DNT header is respected.
 *   - No PII traits (email, name, phone) are ever passed to identify().
 *
 * Set VITE_POSTHOG_KEY in .env to enable; leave unset to disable.
 */

import { sha256 } from './crypto'

declare global {
  interface Window {
    posthog?: {
      capture: (event: string, props?: Record<string, unknown>) => void
      identify: (id: string, props?: Record<string, unknown>) => void
      reset: () => void
      opt_out_capturing: () => void
      opt_in_capturing: () => void
    }
  }
}

let _initialized = false

function _init() {
  if (_initialized) return
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  if (!key || !import.meta.env.PROD) return
  // GDPR: only initialize if user has given consent
  if (localStorage.getItem('bb-cookie-consent') !== 'accepted') return

  // PostHog snippet — loads posthog-js from CDN
  ;(function () {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = 'https://app.posthog.com/static/array.js'
    script.onload = () => {
      if (window.posthog) {
        // @ts-expect-error posthog init is not typed
        window.posthog.init(key, {
          api_host:             'https://app.posthog.com',
          capture_pageview:     true,
          autocapture:          false,  // manual events only — privacy-first
          persistence:          'localStorage',
          disable_session_recording: true,
          respect_dnt:          true,
        })
      }
    }
    document.head.appendChild(script)
  })()

  _initialized = true
}

export const analytics = {
  /** Call once at app startup (called from main.tsx). */
  init: _init,

  /**
   * Identify a logged-in user using a one-way SHA-256 hash of their ID.
   * Raw user IDs and all PII (email, name, etc.) are never forwarded.
   */
  async identify(userId: string) {
    _init()
    const anonymousId = await sha256(`bb:${userId}`)
    window.posthog?.identify(anonymousId)
  },

  /** Track an event. */
  capture(event: string, props?: Record<string, unknown>) {
    _init()
    window.posthog?.capture(event, props)
  },

  /** Reset identity on sign-out. */
  reset() {
    window.posthog?.reset()
  },
}
